/**
 * Live attendance layers (outer → inner in the returned array):
 *   shift  — journey started (role 9 / employee_attandance)
 *   darwin — In-Office / Out Of Office (distance_between_site_eng_to_site)
 *   visit  — site engineer check-in (site_stage, Task, RM, PM, …)
 *
 * DutyBar uses the last entry as the primary (innermost) row.
 */

import { isActiveDistanceCheckin } from './isActiveDistanceCheckin';

export const CHECKED_IN_AT_KEY = 'attendanceCheckedInAt';

const JOURNEY_CHECKOUT_PATH = '/dashboard/check_in';

const DARWIN_MODULE_TYPES = new Set(['In-Office', 'Out Of Office']);

const cap = (value) =>
  typeof value === 'string' && value.length
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : value;

function getProfile(state) {
  return (state && state.loginReducer && state.loginReducer.userProfile) || {};
}

function getActiveDistanceCheckin(state) {
  const profile = getProfile(state);
  return profile.active_distance_checkin || null;
}

function isDarwinModule(moduleType) {
  return DARWIN_MODULE_TYPES.has(moduleType);
}

function resolveModuleType(row) {
  if (!row) return '';
  return row.module_type || row.distance_module_type || row.subject || '';
}

function normalizeOpenDistanceRow(row) {
  if (!row) return null;
  return {
    distance_id: row.distance_id || row.id,
    module_type: resolveModuleType(row),
    site_id: row.site_id,
    lead_id: row.lead_id || row.meet_lead_id,
    site_no: row.site_no,
    client_name: row.client_name || row.client,
    checkin_time: darwinSince(row),
    total_hours: row.total_hours,
  };
}

function getOpenSiteDistanceRow(state) {
  const activeDistance = getActiveDistanceCheckin(state);
  if (activeDistance && !isDarwinModule(activeDistance.module_type)) {
    return activeDistance;
  }
  const darwinStore =
    (state && state.darwinReducer && state.darwinReducer.checkDarwinAttendance) || null;
  if (darwinStore && !isDarwinModule(resolveModuleType(darwinStore))) {
    return normalizeOpenDistanceRow(darwinStore);
  }
  return null;
}

function isOpenPageDistanceRow(d) {
  return isActiveDistanceCheckin(d);
}

function normalizeDarwinLocationType(row) {
  if (!row) return null;
  const raw =
    row.location_type ||
    row.distance_module_type ||
    row.module_type ||
    row.subject ||
    '';
  const s = String(raw).toUpperCase().replace(/_/g, ' ').trim();
  if (s.includes('OUT') && s.includes('OFFICE')) return 'Out Of Office';
  if (s.includes('IN') && s.includes('OFFICE')) return 'In-Office';
  if (raw) return cap(String(raw).replace(/_/g, ' '));
  return null;
}

function checkinMetaAddress(row) {
  if (!row) return null;
  let meta = row.meta_data;
  if (typeof meta === 'string' && meta.trim()) {
    try {
      meta = JSON.parse(meta);
    } catch (e) {
      meta = null;
    }
  }
  if (!meta || typeof meta !== 'object') return null;
  const loc = meta.check_in_location || {};
  const place =
    loc.current_address ||
    loc.office_name ||
    meta.current_address ||
    meta.office_name;
  if (typeof place === 'string' && place.trim()) return place.trim();
  return null;
}

function darwinPlace(row) {
  if (!row) return null;
  const fromMeta = checkinMetaAddress(row);
  if (fromMeta) return fromMeta;
  if (row.office_name) return row.office_name;
  if (row.current_address) return row.current_address;
  const details = row.checkout_details;
  if (details && typeof details === 'object') {
    return details.office_name || details.current_address || null;
  }
  if (typeof details === 'string' && details.trim()) {
    try {
      const parsed = JSON.parse(details);
      return parsed.office_name || parsed.current_address || null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

export function parseAttendanceTime(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const s = String(value).trim();
  const naive = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/
  );
  if (naive && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = new Date(
      Number(naive[1]),
      Number(naive[2]) - 1,
      Number(naive[3]),
      Number(naive[4]),
      Number(naive[5]),
      Number(naive[6])
    ).getTime();
    return Number.isNaN(t) ? null : t;
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
}

function pickLatestDarwinRow(darwinStore, activeDistance) {
  const storeOk = darwinStore && isDarwinModule(resolveModuleType(darwinStore));
  const profileOk =
    activeDistance && isDarwinModule(activeDistance.module_type);
  if (storeOk && profileOk) {
    const storeTs = parseAttendanceTime(darwinSince(darwinStore)) || 0;
    const profileTs = parseAttendanceTime(darwinSince(activeDistance)) || 0;
    return profileTs >= storeTs ? activeDistance : darwinStore;
  }
  if (profileOk) return activeDistance;
  if (storeOk) return darwinStore;
  return null;
}

function darwinSince(row) {
  if (!row) return null;
  return (
    row.distance_check_in_time ||
    row.check_in_time ||
    row.check_in_date_time ||
    row.checkin_time ||
    null
  );
}

export function buildVisitCheckoutPath(row) {
  if (!row) return null;
  if (row.checkout_route) return row.checkout_route;
  const mt = row.module_type;
  const siteId = row.site_id;
  const leadId = row.lead_id;
  const taskId = row.task_id;
  const manageId = row.manage_id || taskId || leadId;

  switch (mt) {
    case 'site_stage':
      if (siteId && leadId) return `/dashboard/site-stage/${siteId}/${leadId}`;
      if (siteId) return `/dashboard/site-stage/${siteId}`;
      return null;
    case 'Task':
      if (taskId) return `/dashboard/site-visit-view/${taskId}`;
      return null;
    case 'cte_ticket':
      if (taskId) return `/dashboard/view-complain-l2/${taskId}`;
      return null;
    case 'RM':
      if (taskId) return `/dashboard/rm-show/${taskId}`;
      if (siteId && leadId) return `/dashboard/site-stage/${siteId}/${leadId}`;
      return null;
    case 'PM':
      if (siteId && manageId) {
        return `/dashboard/view-project-pm-list/${siteId}/${manageId}`;
      }
      return null;
    case 'Meeting Tracker':
      return '/dashboard/brand-activation/scheduled-meeting';
    default:
      if (siteId && leadId) return `/dashboard/site-stage/${siteId}/${leadId}`;
      if (siteId) return `/dashboard/site-stage/${siteId}`;
      return null;
  }
}

function formatSiteDisplay(row) {
  const client = row.client_name;
  const siteNo = row.site_no;
  if (client && siteNo) return `${client} (${siteNo})`;
  if (siteNo) return siteNo;
  if (client) return client;
  if (row.site_id) return `Site #${row.site_id}`;
  return 'Site visit';
}

export function buildVisitLabel(row) {
  if (!row) return 'Checked in';
  const mt = row.module_type;
  const place = formatSiteDisplay(row);
  switch (mt) {
    case 'site_stage':
      return `Checked in · ${place}`;
    case 'Task':
      return `Checked in · Task · ${place}`;
    case 'cte_ticket':
      return `Checked in · Ticket #${row.task_id || '—'}`;
    case 'RM':
      return `Checked in · RM · ${place}`;
    case 'PM':
      return `Checked in · PM · ${place}`;
    case 'Meeting Tracker':
      return `Checked in · Meeting Tracker · ${place}`;
    default:
      return mt ? `Checked in · ${mt} · ${place}` : `Checked in · ${place}`;
  }
}

function mapActiveDistanceToVisit(row, siteDataFallback) {
  const siteData = siteDataFallback || {};
  const name =
    row.client_name && row.site_no
      ? `${row.client_name} (${row.site_no})`
      : formatSiteDisplay(row) ||
        siteData?.lead?.name ||
        siteData?.lead?.company_name ||
        siteData?.name ||
        'Site visit';

  return {
    level: 'visit',
    kind: 'site',
    id: row.distance_id || null,
    siteId: row.site_id || null,
    name,
    label: buildVisitLabel(row),
    since: row.checkin_time || null,
    totalHours: row.total_hours != null ? row.total_hours : null,
    checkOutPath: null,
    checkOutLabel: null,
  };
}

/** Role 9 journey only — never Darwin / site distance. */
function selectJourneyShift(state) {
  const profile = getProfile(state);
  const roles = Array.isArray(profile.roles) ? profile.roles.map((r) => r.id) : [];
  if (!roles.includes(9)) return null;

  const attVal = profile.attVal || profile.att_val || [];
  const activeAtt = attVal[0];
  const hasLegacyCheckIn =
    attVal.length > 0 &&
    activeAtt &&
    (activeAtt.type_id == 1 ||
      String(activeAtt.type || '').toLowerCase() === 'in');

  if (!profile.is_journey_started && !hasLegacyCheckIn) return null;

  const locationType = activeAtt?.location_type || null;
  const place = activeAtt?.address || activeAtt?.details || null;

  let label = 'Journey started';
  if (locationType && place) {
    label = `Journey started : ${cap(locationType)} · ${place}`;
  } else if (place) {
    label = `Journey started : ${place}`;
  } else if (locationType) {
    label = `Journey started : ${cap(locationType)}`;
  }

  return {
    level: 'shift',
    kind: 'journey',
    label,
    since: activeAtt?.check_in_time || activeAtt?.time || null,
    checkOutPath: JOURNEY_CHECKOUT_PATH,
    checkOutLabel: 'End journey',
  };
}

function selectDarwinDuty(state) {
  const darwinStore =
    (state && state.darwinReducer && state.darwinReducer.checkDarwinAttendance) || null;
  const activeDistance = getActiveDistanceCheckin(state);
  const row = pickLatestDarwinRow(darwinStore, activeDistance);

  if (!row) return null;

  const locationType = normalizeDarwinLocationType(row);
  const place = darwinPlace(row);
  let label = locationType || 'On duty';
  if (place) {
    label = `${label} · ${place}`;
  }

  return {
    level: 'darwin',
    kind: 'darwin',
    label,
    since: darwinSince(row),
    checkOutPath: null,
    checkOutLabel: null,
  };
}

function selectSiteVisit(state) {
  const openRow = getOpenSiteDistanceRow(state);
  if (openRow) {
    const slice = (state && state.leadDataReducer) || {};
    return mapActiveDistanceToVisit(openRow, slice.siteData);
  }

  const slice = (state && state.leadDataReducer) || {};
  const d = slice.distanceDetails;
  if (!isOpenPageDistanceRow(d) || isDarwinModule(d.module_type)) return null;

  const siteData = slice.siteData || {};
  const siteName =
    siteData?.lead?.name ||
    siteData?.lead?.company_name ||
    siteData?.lead?.site_code ||
    siteData?.name ||
    (d.site_id ? `Site #${d.site_id}` : 'Site visit');

  return {
    level: 'visit',
    kind: 'site',
    id: d.id || null,
    siteId: d.site_id || null,
    name: siteName,
    label: `Checked in · ${siteName}`,
    since: d.check_in_time || d.checkin_time || d.checkin_date_time || d.created_at || null,
    totalHours: d.total_hours != null ? d.total_hours : null,
    checkOutPath: null,
    checkOutLabel: null,
  };
}

export function selectAttendanceStack(state) {
  const profile = getProfile(state);
  const roles = Array.isArray(profile.roles) ? profile.roles.map((r) => r.id) : [];
  const isRole9 = roles.includes(9);

  const journey = isRole9 ? selectJourneyShift(state) : null;
  const darwin = selectDarwinDuty(state);
  const visit = selectSiteVisit(state);

  if (!isRole9 && !journey) {
    return [darwin, visit].filter(Boolean);
  }

  return [journey, darwin, visit].filter(Boolean);
}

/** True when Darwin In-Office / OOO distance row is open. */
export function selectHasOpenDarwinDuty(state) {
  const darwinStore =
    (state && state.darwinReducer && state.darwinReducer.checkDarwinAttendance) || null;
  if (darwinStore && isDarwinModule(resolveModuleType(darwinStore))) {
    return true;
  }
  const activeDistance = getActiveDistanceCheckin(state);
  return Boolean(activeDistance && isDarwinModule(activeDistance.module_type));
}

/**
 * Shift-only status. Kept for callers that only care whether a shift is open.
 */
export function selectAttendanceStatus(state) {
  const stack = selectAttendanceStack(state);
  const primary = stack[stack.length - 1] || null;
  return {
    isCheckedIn: Boolean(primary),
    label: primary?.label || '',
    since: primary?.since || null,
    checkOutPath: primary?.checkOutPath || null,
  };
}
